import { z } from 'zod';
/**
 * The fixes Bartizo makes itself (P11.18, shared with the root helper in P11.19) so the two ways of
 * running them cannot drift apart: the script a person takes to a server
 * (`fixRunbook` in the dashboard) and the root helper's `apply_compliance_fixes`.
 *
 * A benchmark's remediation is prose, differs per check, and much of it (the
 * firewall, mounts, SSH, the bootloader) can lock a server out, so nothing here
 * is taken from it. Each entry is small, idempotent (run twice, it changes
 * nothing the second time), leaves the server reachable, and keeps a
 * `.bartizo.bak` copy of any file it edits.
 *
 * **The commands are fixed at build time.** A caller names a fix by `id`, from
 * the enum below, and never supplies any text that is run (rule 3): the helper
 * looks the id up here. Adding a fix means adding an entry, which is a release.
 */
/**
 * Whether a fix needs the server restarted before the check passes.
 *
 * `none`: it takes effect when the fix finishes (a service it touches is
 * restarted by the fix itself). `reboot`: the server must be rebooted, so the
 * dashboard shows the fix as applied and pending a restart. The auditing boot
 * options use it; the mount fixes will when they are made safe enough to add.
 */
export const ComplianceRestart = z.enum(['none', 'reboot']);
/** A title as the catalogue holds it: lower case, one space between words, no final stop. */
export const normalTitle = (title) => title.toLowerCase().replace(/\s+/g, ' ').trim().replace(/\.+$/, '').trim();
/**
 * The exit status of a fix that was **not** done because a person should decide
 * (`purge_guarded` found other packages that would go with it). The helper reports
 * it as `skipped` rather than `failed`: nothing is broken.
 */
export const COMPLIANCE_FIX_NEEDS_PERSON = 3;
/**
 * Removes packages only when apt says nothing else would go with them: `$1` is a
 * regex of the package names that may be removed, the rest are the packages (or
 * patterns) to remove. Defined ahead of the fixes, in the script and in the helper.
 */
export const COMPLIANCE_FIX_PRELUDE = [
    'purge_guarded() {',
    '  allowed="$1"; shift',
    "  present=$(dpkg-query -W -f='${binary:Package} ${db:Status-Abbrev}\\n' \"$@\" 2>/dev/null | awk '$2 ~ /^ii/ { print $1 }' | sort -u)",
    '  if [ -z "$present" ]; then echo "   not installed, nothing to do"; return 0; fi',
    '  extra=$(apt-get -s purge $present 2>/dev/null | awk \'/^(Remv|Purg) / { print $2 }\' | grep -Ev "$allowed" || true)',
    `  if [ -n "$extra" ]; then echo "   NOT DONE: removing it would also remove: $(echo $extra | tr '\\n' ' '). A person should decide."; return ${String(COMPLIANCE_FIX_NEEDS_PERSON)}; fi`,
    '  apt-get purge -y $present',
    '}',
].join('\n');
/**
 * Auditing (the "Needs audit rules" checks). Two halves, and the installer's
 * `lib/audit.sh` does the same two things at install time, so a server enrolled
 * after this release has them from the start and an older one gets them from here:
 *
 *   boot options  `audit=1` (record processes that start before auditd) and
 *                 `audit_backlog_limit=8192` (do not drop events when busy), added
 *                 to GRUB_CMDLINE_LINUX beside whatever is already there. A kernel
 *                 option only takes effect at boot, so it marks the server as
 *                 waiting for a reboot the way a package does, and the dashboard
 *                 shows the notice until it has been restarted.
 *   rules         auditd installed, and a small rule set in a file of its own: who
 *                 runs chacl, chcon, setfacl and usermod, and changes to the
 *                 identity files. Few events, so it cannot fill a disk; auditd's
 *                 own log rotation (8 MB x 5 by default) bounds the rest.
 */
const AUDIT_RULES_FILE = '/etc/audit/rules.d/50-bartizo-baseline.rules';
const AUDIT_BOOT_COMMANDS = [
    'f=/etc/default/grub',
    'if [ ! -f "$f" ]; then echo "   NOT DONE: there is no /etc/default/grub here, so the boot option must be set another way. A person should decide."; exit 3; fi',
    '[ -e "$f.bartizo.bak" ] || cp -a "$f" "$f.bartizo.bak"',
    `grep -qE '^GRUB_CMDLINE_LINUX=' "$f" || printf 'GRUB_CMDLINE_LINUX=""\\n' >> "$f"`,
    'changed=0',
    'for opt in audit=1 audit_backlog_limit=8192; do',
    '  key="${opt%%=*}"; want="${opt#*=}"',
    `  cur=$(grep -E '^GRUB_CMDLINE_LINUX=' "$f" | sed -nE "s/.*[\\"' ]\${key}=([^\\"' ]*).*/\\1/p" | head -n1)`,
    '  if [ -n "$cur" ] && { [ "$cur" = "$want" ] || { [ "$key" = audit_backlog_limit ] && [ "$cur" -ge "$want" ] 2>/dev/null; }; }; then continue; fi',
    '  changed=1',
    '  if [ -n "$cur" ]; then',
    `    sed -i -E "/^GRUB_CMDLINE_LINUX=/ s/([\\"' ])\${key}=[^\\"' ]*/\\1\${opt}/" "$f"`,
    '  else',
    `    sed -i -E "/^GRUB_CMDLINE_LINUX=/ s/([\\"'])[[:space:]]*\\$/ \${opt}\\1/" "$f"`,
    '  fi',
    'done',
    `sed -i -E "/^GRUB_CMDLINE_LINUX=/ s/=([\\"'])[[:space:]]+/=\\1/" "$f"`,
    'if [ "$changed" = 1 ]; then',
    '  if command -v update-grub >/dev/null 2>&1; then update-grub; else grub-mkconfig -o /boot/grub/grub.cfg; fi || { cp -a "$f.bartizo.bak" "$f"; echo "   the bootloader did not accept it; /etc/default/grub is back as it was"; exit 1; }',
    'fi',
    'if [ "$changed" = 1 ] || ! grep -qw audit=1 /proc/cmdline; then',
    '  touch /var/run/reboot-required',
    '  grep -qx bartizo-audit /var/run/reboot-required.pkgs 2>/dev/null || echo bartizo-audit >> /var/run/reboot-required.pkgs',
    '  echo "   takes effect after the next reboot"',
    'fi',
].join('\n');
const AUDIT_RULES_COMMANDS = [
    'export DEBIAN_FRONTEND=noninteractive',
    'apt-get install -y --no-install-recommends auditd',
    "UID_MIN=$(awk '/^[[:space:]]*UID_MIN/ { print $2 }' /etc/login.defs)",
    '[ -n "$UID_MIN" ] || UID_MIN=1000',
    `r=${AUDIT_RULES_FILE}`,
    'printf "## Bartizo baseline: a few commands and the identity files, so it stays quiet\\n" > "$r"',
    'for c in /usr/bin/chacl /usr/bin/chcon /usr/bin/setfacl; do',
    '  [ -x "$c" ] && printf -- "-a always,exit -F path=%s -F perm=x -F auid>=%s -F auid!=unset -k perm_chng\\n" "$c" "$UID_MIN" >> "$r"',
    'done',
    '[ -x /usr/sbin/usermod ] && printf -- "-a always,exit -F path=/usr/sbin/usermod -F perm=x -F auid>=%s -F auid!=unset -k usermod\\n" "$UID_MIN" >> "$r"',
    'for w in /etc/group /etc/passwd /etc/gshadow /etc/shadow /etc/sudoers /etc/sudoers.d; do',
    '  [ -e "$w" ] && printf -- "-w %s -p wa -k identity\\n" "$w" >> "$r"',
    'done',
    'chmod 640 "$r"',
    'systemctl enable --now auditd 2>/dev/null || true',
    'augenrules --load || echo "   the rules are written but could not be loaded now (auditing may be locked until a reboot)"',
].join('\n');
const AUDIT_COMMAND_TITLE = (name) => `ensure successful and unsuccessful attempts to use the ${name} command are collected`;
/**
 * The cron files and directories CIS wants root-only. One script for all of them,
 * so they are one task: a path that does not exist is left alone.
 */
const CRON_PERMISSIONS_COMMANDS = [
    'for p in /etc/crontab /etc/cron.hourly /etc/cron.daily /etc/cron.weekly /etc/cron.monthly /etc/cron.d; do',
    '  [ -e "$p" ] || continue',
    '  chown root:root "$p"',
    '  if [ -d "$p" ]; then chmod og-rwx "$p"; else chmod u-x,og-rwx "$p"; fi',
    'done',
].join('\n');
const RSYSLOG_FILE_MODE_COMMANDS = [
    'if ! command -v rsyslogd >/dev/null 2>&1; then echo "   NOT DONE: rsyslog is not installed here. A person should decide."; exit 3; fi',
    'mkdir -p /etc/rsyslog.d',
    `printf '$FileCreateMode 0640\\n' > /etc/rsyslog.d/60-bartizo-filemode.conf`,
    'systemctl reload-or-restart rsyslog',
].join('\n');
const SUDO_USE_PTY_COMMANDS = [
    'if ! command -v visudo >/dev/null 2>&1; then echo "   NOT DONE: sudo is not installed here. A person should decide."; exit 3; fi',
    'mkdir -p /etc/sudoers.d',
    't=$(mktemp)',
    `printf 'Defaults use_pty\\n' > "$t"`,
    // Checked before it is put in place: a bad sudoers file locks everyone out of sudo.
    'if visudo -cf "$t" >/dev/null; then install -m 440 -o root -g root "$t" /etc/sudoers.d/60-bartizo-use-pty; else rm -f "$t"; echo "   the sudoers file did not validate; nothing was changed"; exit 1; fi',
    'rm -f "$t"',
].join('\n');
const SSHD_CONFIG_PERMISSIONS_COMMANDS = [
    'if [ -f /etc/ssh/sshd_config ]; then chown root:root /etc/ssh/sshd_config; chmod u-x,og-rwx /etc/ssh/sshd_config; fi',
].join('\n');
const JOURNALD_CONF = '/etc/systemd/journald.conf';
const purge = (allowed, ...packages) => `purge_guarded '${allowed}' ${packages.map((each) => `'${each}'`).join(' ')}`;
/**
 * Not here on purpose: "journald is not configured to receive logs from a remote
 * client" (CIS Ubuntu 20.04 5.1.1.1.4). It passes only when `systemd-journal-remote`
 * is installed with its socket disabled; on a server without the package (a real
 * one answered "No such file or directory") there is nothing to disable, the check
 * cannot pass, and a fix that does nothing would be offered for ever. Installing a
 * package to turn it off again is not a fix Bartizo makes.
 */
export const COMPLIANCE_FIXES = [
    {
        id: 'journald-forward-to-syslog',
        title: 'ensure journald is configured to send logs to rsyslog',
        does: 'Set ForwardToSyslog=yes in journald.conf (the original is kept as a .bartizo.bak copy), then restart rsyslog',
        commands: [
            `f=${JOURNALD_CONF}`,
            '[ -e "$f.bartizo.bak" ] || cp -a "$f" "$f.bartizo.bak"',
            `grep -q '^\\[Journal\\]' "$f" || printf '[Journal]\\n' >> "$f"`,
            `if grep -qE '^[[:space:]]*#?[[:space:]]*ForwardToSyslog[[:space:]]*=' "$f"; then sed -i -E 's/^[[:space:]]*#?[[:space:]]*ForwardToSyslog[[:space:]]*=.*/ForwardToSyslog=yes/' "$f"; else printf 'ForwardToSyslog=yes\\n' >> "$f"; fi`,
            'systemctl restart systemd-journald',
            'systemctl restart rsyslog 2>/dev/null || true',
        ].join('\n'),
        restart: 'none',
    },
    {
        id: 'bluetooth-disabled',
        title: 'ensure bluetooth is disabled',
        does: 'Stop and mask bluetooth.service',
        commands: 'systemctl stop bluetooth.service 2>/dev/null || true\nsystemctl mask bluetooth.service',
        restart: 'none',
    },
    {
        id: 'telnet-client-removed',
        title: 'ensure telnet client is not installed',
        does: 'Remove the telnet client package, if it is installed and nothing else depends on it',
        commands: purge('^telnet$', 'telnet'),
        restart: 'none',
    },
    {
        id: 'avahi-removed',
        title: 'ensure avahi server is not installed',
        does: 'Stop and remove avahi-daemon, if nothing but its own helpers depends on it',
        commands: [
            'systemctl stop avahi-daemon.service avahi-daemon.socket 2>/dev/null || true',
            purge('^(avahi-|libnss-mdns$)', 'avahi-daemon'),
        ].join('\n'),
        restart: 'none',
    },
    {
        id: 'gdm-removed',
        title: 'ensure gnome display manager is removed',
        does: 'Remove gdm3, if it is installed and nothing else depends on it',
        commands: purge('^(gdm3$|gnome-shell$|ubuntu-desktop|gnome-)', 'gdm3'),
        restart: 'none',
    },
    {
        id: 'xorg-removed',
        title: 'ensure x window system is not installed',
        does: 'Remove the X server packages (xserver-xorg*), if it is only X that goes with them',
        commands: purge('^(xserver-xorg|xorg)', 'xserver-xorg*'),
        restart: 'none',
    },
    {
        id: 'aide-installed',
        title: 'ensure aide is installed',
        does: 'Install AIDE without recommended packages (no mail server is pulled in) and start building its first database in the background, which takes from minutes to an hour',
        // The database is built by a transient unit, not here: `aideinit` reads every
        // file on the server and ran for over half an hour on a real one, which is far
        // longer than a command may hold the helper. The check is for the package; the
        // unit finishes on its own, and `systemctl status bartizo-aide-init` shows it.
        commands: [
            'export DEBIAN_FRONTEND=noninteractive',
            'apt-get install -y --no-install-recommends aide aide-common',
            'if [ ! -f /var/lib/aide/aide.db ] && ! systemctl is-active --quiet bartizo-aide-init.service; then',
            `  systemd-run --quiet --no-block --collect --unit=bartizo-aide-init /bin/sh -c 'aideinit -y -f && if [ -f /var/lib/aide/aide.db.new ]; then mv /var/lib/aide/aide.db.new /var/lib/aide/aide.db; fi'`,
            '  echo "   the AIDE database is being built in the background (unit bartizo-aide-init)"',
            'fi',
        ].join('\n'),
        restart: 'none',
    },
    {
        id: 'core-dumps-restricted',
        title: 'ensure core dumps are restricted',
        does: 'Set a hard core limit of 0, fs.suid_dumpable to 0, and no stored core dumps, in files of its own',
        commands: [
            `printf '* hard core 0\\n' > /etc/security/limits.d/bartizo-coredump.conf`,
            `printf 'fs.suid_dumpable = 0\\n' > /etc/sysctl.d/99-bartizo-coredump.conf`,
            'sysctl -w fs.suid_dumpable=0',
            'mkdir -p /etc/systemd/coredump.conf.d',
            `printf '[Coredump]\\nStorage=none\\nProcessSizeMax=0\\n' > /etc/systemd/coredump.conf.d/bartizo.conf`,
        ].join('\n'),
        restart: 'none',
    },
    {
        id: 'aslr-enabled',
        title: 'ensure address space layout randomization (aslr) is enabled',
        does: 'Set kernel.randomize_va_space to 2, now and at boot, in a file of its own',
        commands: [
            `printf 'kernel.randomize_va_space = 2\\n' > /etc/sysctl.d/99-bartizo-aslr.conf`,
            'sysctl -w kernel.randomize_va_space=2',
        ].join('\n'),
        restart: 'none',
    },
    {
        id: 'audit-boot-before-auditd',
        title: 'ensure auditing for processes that start prior to auditd is enabled',
        does: 'Add audit=1 and audit_backlog_limit=8192 to the kernel options in /etc/default/grub (the original is kept as a .bartizo.bak copy) and rebuild the boot menu; it takes effect after the next reboot',
        commands: AUDIT_BOOT_COMMANDS,
        restart: 'reboot',
    },
    {
        id: 'audit-backlog-limit',
        title: 'ensure audit_backlog_limit is sufficient',
        does: 'Add audit=1 and audit_backlog_limit=8192 to the kernel options in /etc/default/grub (the original is kept as a .bartizo.bak copy) and rebuild the boot menu; it takes effect after the next reboot',
        commands: AUDIT_BOOT_COMMANDS,
        restart: 'reboot',
    },
    {
        id: 'audit-chacl-collected',
        title: AUDIT_COMMAND_TITLE('chacl'),
        does: 'Install auditd and load a small rule set in a file of its own: who runs chacl, chcon, setfacl and usermod, and changes to the user and sudo files',
        commands: AUDIT_RULES_COMMANDS,
        restart: 'none',
    },
    {
        id: 'audit-chcon-collected',
        title: AUDIT_COMMAND_TITLE('chcon'),
        does: 'Install auditd and load a small rule set in a file of its own: who runs chacl, chcon, setfacl and usermod, and changes to the user and sudo files',
        commands: AUDIT_RULES_COMMANDS,
        restart: 'none',
    },
    {
        id: 'cron-crontab-permissions',
        title: 'ensure permissions on /etc/crontab are configured',
        does: 'Make the cron files and directories under /etc root-owned and unreadable to others',
        commands: CRON_PERMISSIONS_COMMANDS,
        restart: 'none',
    },
    {
        id: 'cron-hourly-permissions',
        title: 'ensure permissions on /etc/cron.hourly are configured',
        does: 'Make the cron files and directories under /etc root-owned and unreadable to others',
        commands: CRON_PERMISSIONS_COMMANDS,
        restart: 'none',
    },
    {
        id: 'cron-daily-permissions',
        title: 'ensure permissions on /etc/cron.daily are configured',
        does: 'Make the cron files and directories under /etc root-owned and unreadable to others',
        commands: CRON_PERMISSIONS_COMMANDS,
        restart: 'none',
    },
    {
        id: 'cron-weekly-permissions',
        title: 'ensure permissions on /etc/cron.weekly are configured',
        does: 'Make the cron files and directories under /etc root-owned and unreadable to others',
        commands: CRON_PERMISSIONS_COMMANDS,
        restart: 'none',
    },
    {
        id: 'cron-monthly-permissions',
        title: 'ensure permissions on /etc/cron.monthly are configured',
        does: 'Make the cron files and directories under /etc root-owned and unreadable to others',
        commands: CRON_PERMISSIONS_COMMANDS,
        restart: 'none',
    },
    {
        id: 'cron-d-permissions',
        title: 'ensure permissions on /etc/cron.d are configured',
        does: 'Make the cron files and directories under /etc root-owned and unreadable to others',
        commands: CRON_PERMISSIONS_COMMANDS,
        restart: 'none',
    },
    {
        id: 'sshd-config-permissions',
        title: 'ensure permissions on /etc/ssh/sshd_config are configured',
        does: 'Make /etc/ssh/sshd_config root-owned and unreadable to others, if the file exists',
        commands: SSHD_CONFIG_PERMISSIONS_COMMANDS,
        restart: 'none',
    },
    {
        id: 'rsyslog-file-create-mode',
        title: 'ensure rsyslog log file creation mode is configured',
        does: 'Set $FileCreateMode 0640 in a file of its own in /etc/rsyslog.d, then reload rsyslog',
        commands: RSYSLOG_FILE_MODE_COMMANDS,
        restart: 'none',
    },
    {
        id: 'sudo-use-pty',
        title: 'ensure sudo commands use pty',
        does: 'Add "Defaults use_pty" to a sudoers file of its own, checked with visudo before it is put in place',
        commands: SUDO_USE_PTY_COMMANDS,
        restart: 'none',
    },
    {
        id: 'grub-config-permissions',
        title: 'ensure permissions on bootloader config are configured',
        does: 'Make /boot/grub/grub.cfg root-owned and not readable by others, if the file exists',
        commands: 'if [ -f /boot/grub/grub.cfg ]; then chown root:root /boot/grub/grub.cfg; chmod u-x,go-rwx /boot/grub/grub.cfg; fi',
        restart: 'none',
    },
];
const IDS = COMPLIANCE_FIXES.map((fix) => fix.id);
/** What a command may name. Anything else is not a fix Bartizo knows. */
export const ComplianceFixId = z.enum(IDS);
/** The most fixes one command may carry: every one in the catalogue, with room to grow. */
export const COMPLIANCE_FIXES_MAX_PER_COMMAND = 25;
export const complianceFixById = (id) => COMPLIANCE_FIXES.find((fix) => fix.id === id);
/** The fix for a benchmark check, matched by its title (CIS keeps it across releases). */
export const complianceFixForTitle = (title) => {
    const wanted = normalTitle(title);
    return COMPLIANCE_FIXES.find((fix) => fix.title === wanted);
};
//# sourceMappingURL=compliance-fixes.js.map